<template>
  <div class="flex flex-col">
    <!-- Toolbar -->
    <div class="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
      <div class="flex items-center space-x-4">
        <h2 class="text-lg font-semibold text-gray-900">Patient Coding and Documentation</h2>
        <span class="text-sm text-gray-500">
          {{ totalRows.toLocaleString() }} {{ activeGroupBy ? 'groups' : 'patients' }}
        </span>
        <div class="flex items-center space-x-2 border-l border-gray-200 pl-4">
          <label class="text-xs text-gray-500 whitespace-nowrap">Group by</label>
          <select
            v-model="activeGroupBy"
            @change="onGroupByChange"
            class="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option :value="null">None</option>
            <option v-for="et in entityTypes" :key="et.id" :value="et.id">
              {{ et.type_name }}
            </option>
          </select>
        </div>
        <div class="flex items-center space-x-2 border-l border-gray-200 pl-4">
          <label class="text-xs text-gray-500 whitespace-nowrap">Opp type</label>
          <select
            v-model="activeMetricFilter"
            @change="onMetricFilterChange"
            class="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option :value="null">All</option>
            <option value="30">Resubmissions</option>
            <option value="34">Suspects</option>
            <option value="50">Early Start</option>
          </select>
        </div>
        <div class="flex items-center space-x-2 border-l border-gray-200 pl-4">
          <label class="text-xs text-gray-500 whitespace-nowrap">Hold status</label>
          <select
            v-model="activeHoldFilter"
            @change="onHoldFilterChange"
            class="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option :value="null">All</option>
            <option value="on_hold">On Hold</option>
            <option value="not_on_hold">Not On Hold</option>
          </select>
        </div>
      </div>

      <div class="flex items-center space-x-4">
        <!-- Search -->
        <div class="relative">
          <input
            v-model="searchQuery" data-demo-search
            type="text"
            placeholder="Search by name or MBI..."
            class="sensitive w-64 px-3 py-1.5 pl-9 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            @keyup.enter="performSearch"
          />
          <FontAwesomeIcon
            :icon="['fas', 'magnifying-glass']"
            class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
          />
        </div>
        <button
          v-if="activeSearch"
          @click="clearSearch"
          class="text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          <FontAwesomeIcon :icon="['fas', 'times']" class="mr-1" />
          Clear
        </button>
        <ExportMenu :exports="[{ type: 'excel', action: exportData }]" />
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 flex overflow-hidden">
      <!-- Left: AG-Grid Patient List -->
      <div
        :style="{ width: selectedPatient ? '60%' : '100%' }"
        class="transition-all duration-300 flex flex-col border-r border-gray-200"
        style="height: calc(100vh - 280px)"
      >
        <AgGridVue
          :theme="gridTheme"
          class="flex-1 w-full"
          :column-defs="columnDefs"
          :default-col-def="defaultColDef"
          :grid-options="gridOptions"
          :loading="loading"
          :row-height="48"
          :header-height="48"
          :row-model-type="'serverSide'"
          :get-row-id="getRowId"
          :row-class-rules="rowClassRules"
          @grid-ready="onGridReady"
          @row-clicked="onRowClicked"
        />

        <!-- Footer -->
        <div class="px-4 py-2 border-t border-gray-200 bg-gray-50">
          <div class="flex items-center text-xs">
            <span class="text-gray-600">
              Showing {{ displayedRowCount }} of {{ totalRows.toLocaleString() }} patients
            </span>
          </div>
        </div>
      </div>

      <!-- Right: Patient Detail Panel -->
      <transition
        enter-active-class="transition-all duration-300"
        leave-active-class="transition-all duration-300"
        enter-from-class="opacity-0 translate-x-4"
        leave-to-class="opacity-0 translate-x-4"
      >
        <div
          v-if="selectedPatient"
          style="width: 40%"
          class="flex flex-col overflow-hidden"
          :style="{ height: 'calc(100vh - 280px)' }"
        >
          <Patient_Drawer
            v-if="panelMode === 'patient'"
            :patient-identifier="selectedPatient.patient_id"
            :patient-name="`${selectedPatient.last_name}, ${selectedPatient.first_name}`"
            :enabled-tabs="['hcc_history', 'coding_activity', 'suspects']"
            :default-tab="returnToTab"
            :show-header="true"
            @open-task="viewTask"
            @close="closeDetail"
          />
          <Coding_Opportunity_Drawer
            v-else-if="panelMode === 'opportunity' && selectedOpportunity"
            :task-id="selectedOpportunity.task_id"
            :patient-name="`${selectedPatient.last_name}, ${selectedPatient.first_name}`"
            :opportunity-summary="selectedOpportunity"
            @back="handleBackToPatient"
            @task-completed="handleTaskCompleted"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3';
import { themeQuartz } from 'ag-grid-enterprise';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { isDemoMode, spoofText } from '@/utils/demoSpoof';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import Coding_Opportunity_Drawer from './panels/Coding_Opportunity_Drawer.vue';
import EntityCell from '@/components/widgetcomponents/AG_Grid_Custom_Cells/EntityCell.vue';
import Coding_PatientCell from './cells/Coding_PatientCell.vue';
import Coding_GapCountCell from './cells/Coding_GapCountCell.vue';
import Coding_HoldCountCell from './cells/Coding_HoldCountCell.vue';
import Coding_OpportunityCell from './cells/Coding_OpportunityCell.vue';
import { blockSensitiveEgress } from '@/utils/demoEgress';
import demoProtectedFields from '@/mixins/demoProtectedFields';

export default {
  name: 'Coding_Patient_Codes',
  components: {
    AgGridVue,
    FontAwesomeIcon,
    ExportMenu,
    Patient_Drawer,
    Coding_Opportunity_Drawer,
    EntityCell,
    Coding_PatientCell,
    Coding_GapCountCell,
    Coding_HoldCountCell,
    Coding_OpportunityCell
  },
  mixins: [demoProtectedFields],
  // The patient name/MBI search identifies real patients. Demo Mode empties it and the tagged
  // input is read-only while protected. It is not persisted.
  demoProtectedFields: ['searchQuery'],
  props: {
    entityFilters: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      gridApi: null,
      gridInitialized: false,
      loading: false,
      searchQuery: '',
      activeSearch: null,
      selectedPatient: null,
      panelMode: 'patient',
      selectedOpportunity: null,
      returnToTab: 'hcc_history',
      totalRows: 0,
      displayedRowCount: 0,
      activeGroupBy: null,
      activeMetricFilter: null,
      activeHoldFilter: null,
      entityTypes: [],

      defaultColDef: {
        sortable: true,
        resizable: true,
        filter: false
      },
      columnDefs: [
        {
          headerName: 'Patient',
          field: 'patient_info',
          cellRenderer: 'Coding_PatientCell',
          context: { demoSensitive: true },
          cellClass: 'cell-theme-left-aligned',
          cellStyle: { overflow: 'hidden' },
        },
        {
          headerName: 'Practice / Provider',
          field: 'practice_name',
          cellRenderer: 'EntityCell',
          cellClass: 'cell-theme-left-aligned sensitive',
          cellStyle: { overflow: 'hidden' },
          tooltipValueGetter: params => {
            let p = params.data?.practice_name || '';
            let v = params.data?.provider_name || '';
            // ag-grid renders tooltips in a detached popup the DOM spoof engine
            // never sees, so spoof practice/provider names here in Demo Mode.
            if (isDemoMode()) {
              p = p ? spoofText(p) : p;
              v = v ? spoofText(v) : v;
            }
            return p && v && p !== v ? `${p} / ${v}` : p || v;
          }
        },
        {
          headerName: 'HCCs',
          field: 'total_hccs',
          cellClass: 'cell-theme-center-aligned',
          valueFormatter: params => params.value || '0'
        },
        {
          headerName: 'Open Gaps',
          field: 'open_gaps',
          cellRenderer: 'Coding_GapCountCell',
          cellClass: 'cell-theme-center-aligned',
        },
        {
          headerName: 'Opportunities',
          field: 'opp_total',
          cellRenderer: 'Coding_OpportunityCell',
          cellClass: 'cell-theme-left-aligned',
          cellStyle: { overflow: 'hidden' },
          sortable: false,
        },
        {
          headerName: 'DX Codes',
          field: 'dx_codes_forward',
          cellClass: 'cell-theme-center-aligned',
          valueFormatter: params => params.value || '0'
        },
        {
          headerName: 'QA Rate',
          field: 'qa_pass_rate',
          cellClass: 'cell-theme-center-aligned',
          valueFormatter: params => {
            const rate = params.data?.qa_pass_rate;
            if (rate == null) return '\u2014';
            return `${Math.round(rate * 100)}%`;
          },
          cellStyle: params => {
            const rate = params.data?.qa_pass_rate;
            if (rate == null) return {};
            if (rate >= 0.9) return { color: '#389A6C' };
            if (rate < 0.7) return { color: '#DC2626' };
            return { color: '#D97706' };
          }
        },
        {
          headerName: 'Next Appt',
          field: 'next_appointment_date',
          cellClass: 'cell-theme-center-aligned',
          valueFormatter: params => {
            if (!params.value) return '\u2014';
            const [y, m, d] = params.value.split('-').map(Number);
            return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          },
          cellStyle: params => {
            if (!params.value) return { color: '#9CA3AF' };
            const [y, m, d] = params.value.split('-').map(Number);
            const appt = new Date(y, m - 1, d);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const days = Math.ceil((appt - today) / 86400000);
            if (days <= 3) return { color: '#DC2626', fontWeight: '600' };
            if (days <= 7) return { color: '#D97706', fontWeight: '600' };
            if (days <= 14) return { color: '#389A6C' };
            return {};
          }
        },
        {
          headerName: 'Last Coder',
          field: 'last_coder_name',
          cellClass: 'cell-theme-left-aligned',
          valueFormatter: params => params.value || '\u2014',
          cellStyle: { fontSize: '12px', color: '#6B7280' }
        },
        {
          headerName: 'On Hold',
          field: 'on_hold_count',
          cellRenderer: 'Coding_HoldCountCell',
          cellClass: 'cell-theme-center-aligned',
          // Badge is a short count, so cap the width — sizeColumnsToFit()
          // honours maxWidth and gives the slack to the wider columns. Floor
          // has to clear the header text plus the sort indicator, or "On Hold"
          // truncates to an ellipsis.
          width: 120,
          minWidth: 105,
          maxWidth: 130,
          hide: true
        },
        {
          headerName: 'Hold Reason',
          field: 'hold_reasons',
          cellClass: 'cell-theme-left-aligned',
          valueFormatter: params => params.value || '\u2014',
          cellStyle: { fontSize: '12px', color: '#6B7280' },
          hide: true
        },
        {
          field: 'group_name',
          hide: true,
        },
        {
          headerName: 'Captured',
          field: 'captured',
          cellClass: 'cell-theme-center-aligned',
          cellStyle: { color: '#389A6C' },
          valueFormatter: params => params.value || '0',
          hide: true
        },
        {
          headerName: 'RAF',
          field: 'total_raf',
          cellClass: 'cell-theme-right-aligned',
          valueFormatter: params => params.value?.toFixed(3) || '0.000',
          hide: true
        }
      ],
      gridOptions: {
        rowModelType: 'serverSide',
        suppressHorizontalScroll: false,
        animateRows: true,
        enableCellTextSelection: true,
        ensureDomOrder: true,
        suppressColumnVirtualisation: true,
        tooltipShowDelay: 300,
        components: {
          Coding_PatientCell,
          EntityCell,
          Coding_GapCountCell,
          Coding_HoldCountCell,
          Coding_OpportunityCell
        },
        autoSizeStrategy: {
          type: 'fitGridWidth'
        },
        isServerSideGroup: (dataItem) => dataItem.patient_count !== undefined,
        getServerSideGroupKey: (dataItem) => {
          return String(dataItem.group_name || 'Unassigned');
        },
        getChildCount: (data) => data?.patient_count,
        autoGroupColumnDef: {
          headerName: 'Group',
        },
      },
      rowClassRules: {
        'bg-blue-50': params => params.data?.patient_id === this.selectedPatient?.patient_id
      },
      gridTheme: themeQuartz.withParams({
        browserColorScheme: 'light',
        backgroundColor: '#ffffff',
        foregroundColor: '#1f2937',
        borderColor: '#e5e7eb',
        borderRadius: 0,
        fontFamily: 'inherit',
        fontSize: '14px',
        headerBackgroundColor: '#f9fafb',
        headerFontSize: '12px',
        headerFontWeight: 600,
        oddRowBackgroundColor: '#ffffff',
        rowBorder: true,
        rowHoverColor: '#f3f4f6',
        spacing: 8,
        wrapperBorder: false
      })
    };
  },
  async mounted() {
    const response = await this.$axios.get('/api/entity-types/', { params: { include_counts: true } });
    this.entityTypes = (response.data || []).filter(et => et.entity_count > 0);
  },
  watch: {
    entityFilters() {
      if (!this.gridInitialized) return;
      this.selectedPatient = null;
      this.panelMode = 'patient';
      this.selectedOpportunity = null;
      this.refreshGrid();
    },
    selectedPatient(newVal, oldVal) {
      // Reset to patient mode when patient changes (skip if opening opportunity directly)
      if (newVal?.patient_id !== oldVal?.patient_id && !this._skipPatientReset) {
        this.panelMode = 'patient';
        this.selectedOpportunity = null;
        this.returnToTab = 'hcc_history';
      }
      if (this.gridApi) {
        this.gridApi.redrawRows();
        setTimeout(() => {
          this.fitColumns();
        }, 350);
      }
    }
  },
  methods: {
    fitColumns() {
      if (!this.gridApi) return;
      this.gridApi.sizeColumnsToFit();
    },

    getRowId(params) {
      if (params.data.patient_count !== undefined) {
        return `group_${params.data.group_name || 'unknown'}`;
      }
      return params.data.patient_id;
    },

    onGridReady(params) {
      this.gridApi = params.api;

      const datasource = {
        getRows: async (request) => {
          try {
            this.loading = true;
            const response = await this.$axios.post('/api/coding/patients/', {
              startRow: request.request.startRow,
              endRow: request.request.endRow,
              sortModel: request.request.sortModel,
              search: this.activeSearch || undefined,
              entity_filters: this.entityFilters || undefined,
              groupByEntityTypeId: this.activeGroupBy || undefined,
              groupKeys: request.request.groupKeys || [],
              metricFilter: this.activeMetricFilter || undefined,
              holdFilter: this.activeHoldFilter || undefined,
            });

            // Only update top-level counts (not child row loads from group expansion)
            if (!request.request.groupKeys?.length) {
              this.totalRows = response.data.lastRow || 0;
              this.displayedRowCount = response.data.rowData?.length || 0;
            }

            request.success({
              rowData: response.data.rowData || [],
              lastRow: response.data.lastRow || 0
            });

            setTimeout(() => {
              this.fitColumns();
            }, 100);
          } catch (error) {
            console.error('Failed to fetch patients:', error);
            request.fail();
          } finally {
            this.loading = false;
            this.gridInitialized = true;
          }
        }
      };

      this.gridApi.setGridOption('serverSideDatasource', datasource);

      window.addEventListener('resize', () => {
        this.fitColumns();
      });
    },

    refreshGrid() {
      if (this.gridApi) {
        this.gridApi.refreshServerSide({ purge: true });
      }
    },

    onRowClicked(event) {
      if (event.data?.patient_count !== undefined) return;
      this.selectedPatient = event.data;
    },

    closeDetail() {
      this.selectedPatient = null;
      this.panelMode = 'patient';
      this.selectedOpportunity = null;
    },

    performSearch() {
      this.activeSearch = this.searchQuery || null;
      this.selectedPatient = null;
      this.refreshGrid();
    },

    clearSearch() {
      this.searchQuery = '';
      this.activeSearch = null;
      this.selectedPatient = null;
      this.refreshGrid();
    },

    onMetricFilterChange() {
      this.selectedPatient = null;
      this.panelMode = 'patient';
      this.selectedOpportunity = null;
      this.refreshGrid();
    },

    onHoldFilterChange() {
      const showHoldCols = this.activeHoldFilter === 'on_hold';
      this.gridApi.applyColumnState({
        state: [
          { colId: 'on_hold_count', hide: !showHoldCols },
          { colId: 'hold_reasons', hide: !showHoldCols },
        ],
      });
      this.selectedPatient = null;
      this.panelMode = 'patient';
      this.selectedOpportunity = null;
      this.refreshGrid();
    },

    onGroupByChange() {
      if (!this.gridApi) return;

      // Toggle group_name column as row group based on active selection
      this.gridApi.applyColumnState({
        state: [{
          colId: 'group_name',
          rowGroup: !!this.activeGroupBy,
          rowGroupIndex: this.activeGroupBy ? 0 : null,
          hide: true,
        }],
      });

      this.selectedPatient = null;
      this.panelMode = 'patient';
      this.selectedOpportunity = null;
      this.refreshGrid();
    },

    viewTask(opportunityContext) {
      this.selectedOpportunity = opportunityContext;
      this.panelMode = 'opportunity';
    },

    handleBackToPatient() {
      this.returnToTab = 'coding_activity';
      this.panelMode = 'patient';
      this.selectedOpportunity = null;
    },

    handleTaskCompleted() {
      this.returnToTab = 'coding_activity';
      this.panelMode = 'patient';
      this.selectedOpportunity = null;
      this.refreshGrid();
    },

    exportData() {
      if (blockSensitiveEgress('export')) return;
      this.gridApi.exportDataAsCsv({
        fileName: `coding_patients_${new Date().toISOString().split('T')[0]}.csv`
      });
    }
  }
};
</script>
